import { onBeforeUnmount, ref } from 'vue';
import { useEventListener } from '@vueuse/core';
import { sendMindboxEvent } from '@experience/core-consent-package';
import type { MindboxSearchCustomFields } from '@/pages/listing/lib/mindbox/shared/types';

const SEARCH_OPERATION = 'SearchExcursionsListing';

/** Задержка отправки по таймеру при неактивности (вкладка скрыта), мс */
const INACTIVITY_SEND_DELAY_MS = 30 * 60 * 1_000;

/** Геттер: возвращает payload поиска для Mindbox или null (не отправлять) */
type GetSearchPayload = () => MindboxSearchCustomFields | null;

/**
 * Отправляет событие Mindbox «SearchExcursionsListing» один раз за сессию листинга при уходе пользователя:
 * - pagehide / beforeunload (закрытие вкладки, переход)
 * - onBeforeUnmount (смена маршрута в SPA)
 * - visibilitychange: при скрытии вкладки запускается таймер; если через INACTIVITY_SEND_DELAY_MS вкладка всё ещё скрыта — отправка
 * Дедупликация: только одна отправка за сессию (срабатывает первый триггер).
 *
 * @param getPayload - функция, возвращающая customerAction.customFields или null
 * @returns { sent } - ref-флаг, что событие уже отправлено
 */
export const useMindboxSearchEvent = (getPayload: GetSearchPayload) => {
  const sent = ref(false);
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (sent.value) {
      return;
    }
    const payload = getPayload();
    if (!payload) {
      return;
    }
    sent.value = true;
    sendMindboxEvent({
      operation: SEARCH_OPERATION,
      data: {
        customerAction: {
          customFields: payload,
        },
      },
    });
  };

  const clearInactivityTimer = () => {
    if (inactivityTimer !== null) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      clearInactivityTimer();
      inactivityTimer = setTimeout(() => {
        inactivityTimer = null;
        flush();
      }, INACTIVITY_SEND_DELAY_MS);
    } else {
      clearInactivityTimer();
    }
  };

  useEventListener(window, 'pagehide', flush);
  useEventListener(window, 'beforeunload', flush);
  useEventListener(document, 'visibilitychange', onVisibilityChange);

  onBeforeUnmount(() => {
    clearInactivityTimer();
    flush();
  });

  return { sent };
};
